import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import {
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  ListItem,
  Typography,
} from "@mui/material";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import PropTypes from "prop-types";
import { isEqual } from "lodash";
import { withTranslation } from "react-i18next";

import { useLazyUtilityIssueByUtilityIdQuery } from "apis/UtilityIssue";
import { usePostConsumerComplaintMutation } from "apis/ConsumerComplaint";
import { useUtilityQuery } from "apis/Utility";
import { useConsumersQuery } from "apis/User";
import InputFieldText from "Components/Common/InputFieldText";
import SelectField from "Components/Common/SelectField";
import { severityOptions } from "common/enums";
import { complaintValidationSchema } from "./ComplaintValidationSchema";
import { filterEmptyStrings } from "utils";

const AddConsumerComplaint = ({ setActiveTab, t }) => {
  const [utilityOptions, setAllUtilitiesOption] = useState([]);

  const {
    data: { data: consumerNumbers } = {},
    isLoading: isConsumerNumbersLoading,
  } = useConsumersQuery();
  const {
    data: { data: { items: utilityList } = {} } = {},
    isLoading: isUtilityListLoading,
  } = useUtilityQuery();
  const [addConsumerComplaint, { isLoading }] =
    usePostConsumerComplaintMutation();
  const [
    getAllUtilityIssue,
    { data: { data: utilityIssues } = {}, isLoading: isUtilityIssuesLoading },
  ] = useLazyUtilityIssueByUtilityIdQuery();

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      utilityId: 0,
      utilityIssueId: "",
      consumerNumber: "",
      severity: "",
      description: "",
      complaintDocuments: [],
    },
    validationSchema: complaintValidationSchema,
    onSubmit: async (values) => {
      try {
        const formData = new FormData();

        const payload = filterEmptyStrings({
          ...values,
          utilityIssueId:
            values.utilityIssueId === "otherIssue" ? "" : values.utilityIssueId,
        });

        Object.entries(payload).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            value.forEach((item, index) => {
              formData.append(`${key}[${index}].id`, index);
              formData.append(`${key}[${index}].file`, item);
            });
          } else {
            formData.append(key, value);
          }
        });
        await addConsumerComplaint(formData);
        setActiveTab(0);
      } catch (err) {}
    },
  });

  const {
    values: { complaintDocuments, utilityId, utilityIssueId },
    errors,
    setFieldValue,
    handleSubmit,
  } = formik;

  const getUtilityIssueByUtilityId = async (id) => {
    try {
      await getAllUtilityIssue(id);
    } catch (err) {}
  };

  const handleDelete = (chipToDelete) => () => {
    setFieldValue(
      "complaintDocuments",
      complaintDocuments.filter((chip) => !isEqual(chip, chipToDelete))
    );
  };

  useEffect(() => {
    if (utilityId) {
      const otherOption = {
        id: "otherIssue",
        name: "Other",
        utilityId: utilityId,
      };
      setAllUtilitiesOption([...utilityIssues, otherOption]);
    }
  }, [utilityIssues]);

  useEffect(() => {
    if (utilityId) {
      setFieldValue("utilityIssueId", "");
      getUtilityIssueByUtilityId(utilityId);
    }
  }, [utilityId]);

  useEffect(() => {
    if (utilityIssueId) {
      setFieldValue("description", "");
    }
  }, [utilityIssueId]);

  return (
    <Card className="h-100 mt-3">
      <form onSubmit={handleSubmit}>
        <CardContent className="overflow-auto h-70">
          <Grid
            container
            spacing={2}
            rowSpacing={1}
            columnSpacing={{ xs: 1, sm: 2, md: 3 }}
          >
            <Grid item xs={12} sm={4}>
              <SelectField
                label={t("Utility") + "*"}
                name="utilityId"
                formik={formik}
                options={utilityList || []}
                loading={isUtilityListLoading}
                getOptionLabel={(option) => option?.name}
                getOptionValue={(option) => option?.id}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <SelectField
                label={t("Priority") + "*"}
                name="severity"
                formik={formik}
                options={severityOptions || []}
                getOptionLabel={(option) => option.label}
                getOptionValue={(option) => option.value}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <SelectField
                label={t("Customer Number") + "*"}
                name="consumerNumber"
                formik={formik}
                options={consumerNumbers || []}
                loading={isConsumerNumbersLoading}
                getOptionLabel={(option) => option.consumerNumber}
                getOptionValue={(option) => option.consumerNumber}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <SelectField
                label={t("Issues") + "*"}
                name="utilityIssueId"
                formik={formik}
                options={utilityOptions || []}
                loading={isUtilityIssuesLoading}
                getOptionLabel={(option) => option.name}
                getOptionValue={(option) => option.id}
                disabled={utilityId === 0}
              />
            </Grid>
            <Grid item xs={12}>
              <InputFieldText
                placeholder={`${"Please enter Description"}${
                  utilityIssueId === "otherIssue" ? "*" : ""
                }`}
                name="description"
                formik={formik}
                multiline
                minRows={3}
                maxRows={6}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                color="primary"
                size="small"
                variant="outlined"
                component="label"
              >
                <FileUploadIcon />
                {t("Upload")}
                <input
                  type="file"
                  name="complaintDocuments"
                  onChange={(e) => {
                    const newSelectedFiles = Array.from(e.target.files);
                    const updatedData = [
                      ...complaintDocuments,
                      ...newSelectedFiles,
                    ];
                    setFieldValue("complaintDocuments", updatedData);
                    e.target.value = null;
                  }}
                  multiple
                  hidden
                />
              </Button>
              <Grid container>
                {complaintDocuments.map((data, index) => (
                  <Grid item padding="12px 10px 5px 0px" key={index}>
                    <ListItem key={data.name} sx={{ padding: "0px" }}>
                      <Chip label={data.name} onDelete={handleDelete(data)} />
                    </ListItem>
                  </Grid>
                ))}
              </Grid>
              {errors.complaintDocuments ? (
                <Typography fontSize="12px" color="error" mt="5px">
                  {t(`${errors.complaintDocuments}`)}
                </Typography>
              ) : null}
            </Grid>
          </Grid>
        </CardContent>
        <CardActions className="d-flex justify-content-end px-4 pb-3">
          <Button
            variant="contained"
            type="submit"
            size="small"
            disabled={isLoading}
          >
            {t("Submit")}
            {isLoading && <CircularProgress size={20} className="mx-1" />}
          </Button>
        </CardActions>
      </form>
    </Card>
  );
};

AddConsumerComplaint.propTypes = {
  setActiveTab: PropTypes.func,
  t: PropTypes.func,
};

export default withTranslation()(AddConsumerComplaint);
